import axiosInstance from "./axios-instance";
import { SiddurTree } from "@shared/types";

const CACHE_KEY = "siddur-ashkenaz-tree";

interface TreeResponse {
  version: string;
  data: SiddurTree;
}

/**
 * The cached copy, or nothing.
 *
 * Anything unreadable is treated as absent rather than thrown: a bad cache
 * entry should cost one extra download, not a blank screen.
 */
const readCache = (): TreeResponse | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TreeResponse;
    return parsed?.version && parsed?.data?.root ? parsed : null;
  } catch {
    return null;
  }
};

const writeCache = (payload: TreeResponse): void => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Storage full or blocked. The tree still works, it just downloads again.
  }
};

export const getSiddurVersion = async (): Promise<string> => {
  const { data } = await axiosInstance.get<{ version: string }>("/get-siddur-tree/version");
  return data.version;
};

/**
 * The Siddur tree, from cache when the server's version still matches.
 *
 * The version is a hash of the file the server reads, so corrected text can
 * never sit behind a stale cache the way it did when the number was raised by
 * hand. One small request decides; the megabyte only moves when it has to.
 */
export const getSiddurTree = async (): Promise<TreeResponse> => {
  const cached = readCache();

  if (cached) {
    try {
      const version = await getSiddurVersion();
      if (version === cached.version) return cached;
    } catch {
      return cached; // Offline or the server is down: read what we have.
    }
  }

  const { data } = await axiosInstance.get<TreeResponse>("/get-siddur-tree");
  writeCache(data);
  return data;
};
